/**
 * The business day, as the ERP keeps it (v7 CORE-0 §5): the organisation's own date, IST, on the server's clock.
 *
 * Screens used to work "today" and "tomorrow" out from this computer's clock plus 5:30. That is right only while the
 * computer's clock is. Now every screen asks this module, which is anchored to api.business_day() (read by the top bar
 * every five minutes): the server's time, the organisation's time zone. Until the first answer arrives it falls back to
 * this computer's clock in IST, which is what every screen did before.
 */
import { useQuery } from '@tanstack/react-query';
import { rpc } from './supabase';

export interface BusinessDay {
  today: string; tomorrow: string; now: string; timezone: string; cutoffTime: string; tomorrowCutoffAt: string; environment?: 'PRODUCTION' | 'STAGING' | 'TEST';
  /** 145550: tomorrow as a whole. OPEN: still to be prepared. PREPARED: orders and rounds made, changes go straight until the cut-off. CLOSED: the cut-off ran or its clock passed. */
  tomorrowState?: 'OPEN' | 'PREPARED' | 'CLOSED';
  /** The first day an agreement can start on (one customer's own day can be later: the server's assert_day_open decides). */
  firstOpenDay?: string;
  /** Staff work the day that began until this time that morning: the plant's packing day closes then (plant.packing_short_at). */
  staffDayOpenUntil?: string;
}

let offsetMs = 0;
let zone = 'Asia/Kolkata';

/** The server's "now", in milliseconds. */
export const serverNow = (): number => Date.now() + offsetMs;

/** The business date `offset` days from today, as YYYY-MM-DD. */
export function istDay(offset = 0): string {
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(serverNow()));
  return offset === 0 ? today : new Date(Date.parse(`${today}T00:00:00Z`) + offset * 86_400_000).toISOString().slice(0, 10);
}

/** Today's business date, as YYYY-MM-DD. */
export const istToday = (): string => istDay(0);

/** The cut-off in words, from the server: "22:30", or "the cut-off" until the first answer. A screen never states a time of its own. */
export const cutoffWords = (day: BusinessDay | undefined): string => day?.cutoffTime?.slice(0, 5) ?? 'the cut-off';

/** Reads the business day and anchors this module to it. Mounted once, in the top bar. */
export function useBusinessDay() {
  return useQuery({
    queryKey: ['business-day'],
    staleTime: 300_000,
    refetchInterval: 300_000,
    retry: false,
    queryFn: async () => {
      const day = await rpc<BusinessDay>('business_day', {});
      offsetMs = Date.parse(day.now) - Date.now();
      zone = day.timezone || 'Asia/Kolkata';
      return day;
    },
  });
}
